import { useMemo } from 'react'
import { DagDropdownCell } from './DagCellRenderer'
import type { HierarchyForDagData } from '@/hooks/useHierarchyForDag'
import type { DropdownOptionRead } from '@/types/dag'
import {
  getHierarchyOptionsForLevel,
  type HierarchyLevel,
} from './hierarchyDropdownUtils'

interface HierarchyDropdownCellProps {
  value: unknown
  hierarchyLevel: HierarchyLevel
  rowCells: Record<string, unknown>
  hierarchyData: HierarchyForDagData
  onSaveWithCascade: (
    updates: Array<{ columnId: string; value: unknown }>
  ) => void
  onCancel: () => void
  onTab?: (shiftKey: boolean) => boolean | void
  onEnter?: (shiftKey: boolean) => void
}

/**
 * Cascading hierarchy dropdown for department → class → subclass columns.
 * Filters options based on sibling column values and auto-clears children on change.
 */
export function HierarchyDropdownCell({
  value,
  hierarchyLevel,
  rowCells,
  hierarchyData,
  onSaveWithCascade,
  onCancel,
  onTab,
  onEnter,
}: HierarchyDropdownCellProps) {
  const options: DropdownOptionRead[] = useMemo(
    () => getHierarchyOptionsForLevel(hierarchyLevel, rowCells, hierarchyData),
    [hierarchyLevel, rowCells, hierarchyData]
  )

  const handleSave = (selectedValue: unknown) => {
    const newValue = selectedValue as string

    switch (hierarchyLevel) {
      case 'division':
        onSaveWithCascade([
          { columnId: 'division', value: newValue },
          { columnId: 'department', value: null },
          { columnId: 'class', value: null },
          { columnId: 'subclass', value: null },
        ])
        break
      case 'department':
        onSaveWithCascade([
          { columnId: 'department', value: newValue },
          { columnId: 'class', value: null },
          { columnId: 'subclass', value: null },
        ])
        break
      case 'class':
        onSaveWithCascade([
          { columnId: 'class', value: newValue },
          { columnId: 'subclass', value: null },
        ])
        break
      case 'subclass':
        onSaveWithCascade([{ columnId: 'subclass', value: newValue }])
        break
    }
  }

  return (
    <DagDropdownCell
      value={value}
      options={options}
      onSave={handleSave}
      onCancel={onCancel}
      onTab={onTab}
      onEnter={onEnter}
    />
  )
}
